/* ==========================================================================
   Future Aesthetics — <fa-carousel>
   A scroll-snap track with previous/next buttons. No dependencies: the track
   is a real horizontally scrollable list, so keyboard, trackpad and touch all
   work before the JS runs, and the buttons are progressive enhancement.

   Markup contract:
     <fa-carousel>
       <div data-carousel-track> …items… </div>
       <button data-carousel-prev>  (optional)
       <button data-carousel-next>  (optional)
     </fa-carousel>
   ========================================================================== */

(function () {
  'use strict';

  class FaCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');

      this.prevButton?.addEventListener('click', () => this.scrollByPage(-1));
      this.nextButton?.addEventListener('click', () => this.scrollByPage(1));

      this.updateHandler = this.update.bind(this);
      this.track.addEventListener('scroll', this.updateHandler, { passive: true });
      window.addEventListener('resize', this.updateHandler);

      // Items are lazy-sized by CSS; wait a frame so the first measurement is real.
      requestAnimationFrame(this.updateHandler);
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.updateHandler);
      window.removeEventListener('resize', this.updateHandler);
    }

    get step() {
      const item = this.track.firstElementChild;
      if (!item) return this.track.clientWidth;

      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    scrollByPage(direction) {
      // Advance by whole cards, a viewport at a time, without overshooting.
      const step = this.step;
      const perPage = Math.max(1, Math.floor(this.track.clientWidth / step));
      this.track.scrollBy({ left: direction * step * perPage, behavior: 'smooth' });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const maxScroll = scrollWidth - clientWidth;
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft >= maxScroll - 1;
      const overflows = maxScroll > 1;

      this.toggleAttribute('data-overflows', overflows);

      if (this.prevButton) this.prevButton.disabled = atStart || !overflows;
      if (this.nextButton) this.nextButton.disabled = atEnd || !overflows;
    }
  }

  if (!customElements.get('fa-carousel')) {
    customElements.define('fa-carousel', FaCarousel);
  }
})();
